import { z } from 'zod';
import { optionalPoint } from './geo.js';
/** What a saved place is. Only airports and other places may show as buttons on a driver's public page. */
export const PLACE_KINDS = ['airport', 'home', 'work', 'friend', 'other'];
export const PAGE_PLACE_KINDS = ['airport', 'other'];
export const MAX_PLACES = 20;
/** The empty buttons a new list offers, in this order. */
export const PLACE_SLOTS = [
    { kind: 'home', label: 'Home 1' },
    { kind: 'home', label: 'Home 2' },
    { kind: 'work', label: 'Work 1' },
    { kind: 'work', label: 'Work 2' },
    { kind: 'friend', label: 'Friend 1' },
    { kind: 'friend', label: 'Friend 2' },
];
/** Airports a Southern California driver or rider adds with one tap; the terminal loop addresses. */
export const AIRPORTS = [
    { code: 'LAX', label: 'LAX Airport', address: '1 World Way, Los Angeles, CA 90045', point: { lat: 33.9416, lng: -118.4085 } },
    { code: 'SNA', label: 'SNA (John Wayne)', address: '18601 Airport Way, Santa Ana, CA 92707', point: { lat: 33.6762, lng: -117.8675 } },
    { code: 'LGB', label: 'Long Beach Airport', address: '4100 Donald Douglas Dr, Long Beach, CA 90808', point: { lat: 33.8177, lng: -118.1516 } },
    { code: 'BUR', label: 'Burbank Airport', address: '2627 N Hollywood Way, Burbank, CA 91505', point: { lat: 34.2007, lng: -118.3587 } },
    { code: 'ONT', label: 'Ontario Airport', address: '2500 E Airport Dr, Ontario, CA 91761', point: { lat: 34.056, lng: -117.6012 } },
    { code: 'SAN', label: 'San Diego Airport', address: '3225 N Harbor Dr, San Diego, CA 92101', point: { lat: 32.7336, lng: -117.1897 } },
];
/** The airport at a point, when the point is within about a mile of one we know. */
export function airportAt(p) {
    if (!p)
        return null;
    const rad = (d) => (d * Math.PI) / 180;
    return (AIRPORTS.find((a) => {
        const h = Math.sin(rad(a.point.lat - p.lat) / 2) ** 2 + Math.cos(rad(p.lat)) * Math.cos(rad(a.point.lat)) * Math.sin(rad(a.point.lng - p.lng) / 2) ** 2;
        return 2 * 6_371_000 * Math.asin(Math.sqrt(h)) < 1600;
    }) ?? null);
}
/** A trip to or from an airport, by the name or code in an address or by where its places are: the airport fee applies. */
export const airportTrip = (stops) => stops.some((s) => /airport|\b(LAX|SNA|LGB|BUR|ONT|SAN)\b/i.test(s.address) || airportAt(s.point) !== null);
export const PlaceSchema = z
    .object({
    kind: z.enum(PLACE_KINDS),
    label: z.string().trim().min(1).max(40),
    /** The same rule as a booking's pickup and drop-off. */
    address: z.string().trim().min(3).max(300),
    /** Where the address is, when it was picked from a suggestion. */
    point: optionalPoint,
    showOnPage: z.boolean().optional().default(false),
})
    .refine((p) => !p.showOnPage || PAGE_PLACE_KINDS.includes(p.kind), {
    message: 'only airports and other places can show on your booking page',
    path: ['showOnPage'],
});
/** The whole list at once, in the order the buttons show. */
export const PlacesInputSchema = z.object({
    places: z
        .array(PlaceSchema)
        .max(MAX_PLACES, `up to ${MAX_PLACES} places`)
        .refine((list) => new Set(list.map((p) => p.label.toLowerCase())).size === list.length, {
        message: 'each place needs its own name',
    }),
});
/** The address a passenger shares with drivers: their first home place. */
export const homeAddressOf = (places) => places.find((p) => p.kind === 'home')?.address ?? null;
//# sourceMappingURL=places.js.map